<template>
  <div class="import-configure">
    <p>Map packaged databases to an existing destination or create new ones.</p>

    <p v-if="destinationLocked" class="destination-note">
      This extension contains tasks or pages, so it installs into your private
      organization.
    </p>
    <div v-else class="pickers">
      <FormInputGroup label="Organization" for-id="import-org">
        <BaseInputSelect
          :value="orgIdModel ?? ''"
          :options="orgOptions"
          :disabled="orgsLoading"
          :debounce-ms="0"
          select-only
          searchable
          placeholder="Select organization"
          @update:value="(v) => (orgIdModel = v as OrgId)"
        />
      </FormInputGroup>
      <FormInputGroup
        v-if="orgIdModel"
        label="Workspace"
        for-id="import-workspace"
      >
        <BaseInputSelect
          :value="workspaceIdModel ?? ''"
          :options="workspaceOptions"
          :disabled="workspacesLoading || workspaceOptions.length === 0"
          :debounce-ms="0"
          select-only
          searchable
          placeholder="Select workspace"
          @update:value="(v) => (workspaceIdModel = v as WorkspaceId)"
        />
      </FormInputGroup>
    </div>

    <div v-if="showAgentPicker && orgIdModel" class="agent-picker">
      <FormInputGroup label="Agent" for-id="import-agent">
        <BaseInputSelect
          :value="automationAgentId ?? ''"
          :options="agentOptions"
          :debounce-ms="0"
          select-only
          searchable
          placeholder="No agent"
          @update:value="onAgentSelected"
        />
      </FormInputGroup>
    </div>

    <div v-if="workspaceIdModel && pkg && dbItems.length > 0" class="dbs">
      <div class="section-title">Databases</div>
      <div class="db-table">
        <details v-for="db in dbItems" :key="db.packagedDbId" class="db">
          <summary class="db-row">
            <div class="db-left">
              <ChevronDownIcon class="chevron" />

              <BaseTypeIcon
                class="icon"
                :image="db.packagedIcon ?? undefined"
              />
              <div class="db-title">
                <span class="db-name">{{ db.packagedName }}</span>
                <template v-if="db.mode !== 'create' && db.workspaceName">
                  <span class="db-arrow">-&gt;</span>
                  <BaseTypeIcon
                    class="icon secondary"
                    :image="db.workspaceIcon ?? undefined"
                  />
                  <span class="db-name secondary">{{ db.workspaceName }}</span>
                </template>
                <span
                  v-if="(modeByDbId[db.packagedDbId] || 'create') === 'create'"
                  class="new"
                >
                  New
                </span>
                <span
                  v-if="conflictByDbId[db.packagedDbId]"
                  class="conflict-badge"
                  >Conflict</span
                >
              </div>
            </div>
            <div class="db-right">
              <span
                class="db-count"
                :class="{
                  plus:
                    db.isRequired ||
                    importObjectsEnabledByDbId[db.packagedDbId] !== false,
                }"
              >
                <template
                  v-if="
                    db.isRequired ||
                    importObjectsEnabledByDbId[db.packagedDbId] !== false
                  "
                >
                  +{{ db.objectsCount }} object{{
                    db.objectsCount === 1 ? "" : "s"
                  }}
                </template>
                <template v-else>0 objects</template>
              </span>
              <BaseToggleButton
                class="toggle"
                :checked="
                  db.isRequired ||
                  importObjectsEnabledByDbId[db.packagedDbId] !== false
                "
                checked-label="Import"
                unchecked-label="Skip"
                :disabled="db.isRequired"
                :help="
                  db.isRequired
                    ? 'This database is required by the exported object hierarchy.'
                    : undefined
                "
                @change="(v) => setImportObjectsEnabled(db.packagedDbId, v)"
              />
            </div>
          </summary>

          <div class="db-details">
            <ImportDatabase
              :packaged-db-id="db.packagedDbId"
              :packaged-db="pkg.databases[db.packagedDbId]"
              :objects-count="db.objectsCount"
              :workspace-db-dict="workspaceDbDict"
              :mode="modeByDbId[db.packagedDbId] || 'create'"
              :selected-target-id="targetByDbId[db.packagedDbId]"
              :hidden-database-ids="
                hiddenTargetsByPackagedDbId[db.packagedDbId] || []
              "
              @update:mode="(m) => setMode(db.packagedDbId, m)"
              @update:selectedTargetId="(v) => setTarget(db.packagedDbId, v)"
            />
          </div>
        </details>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  ref,
  watch,
} from "vue";

import type {
  AnyAgentId,
  FullOrganization,
  FullWorkspace,
  GraphDatabaseDictionary,
  OrgId,
  TableId,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import BaseInputSelect, {
  type SelectOption,
} from "@/components/base/BaseInputSelect.vue";
import BaseToggleButton from "@/components/base/BaseToggleButton.vue";
import BaseTypeIcon from "@/components/base/BaseTypeIcon.vue";
import FormInputGroup from "@/components/graph/definition/form/FormInputGroup.vue";
import ChevronDownIcon from "@/components/icons/ChevronDownIcon.vue";
import * as inject from "@/components/inject";
import type { InstallDatabaseMode } from "@/components/marketplace/import";
import ImportDatabase from "@/components/marketplace/ImportDatabase.vue";
import type { Package } from "@/components/marketplace/types";
import { typeDiff } from "@/components/marketplace/utils";

type Mode = InstallDatabaseMode;

export default defineComponent({
  name: "ImportConfigure",
  components: {
    BaseTypeIcon,
    BaseInputSelect,
    BaseToggleButton,
    ChevronDownIcon,
    FormInputGroup,
    ImportDatabase,
  },
  props: {
    orgId: { type: String as PropType<OrgId | undefined>, required: false },
    workspaceId: {
      type: String as PropType<WorkspaceId | undefined>,
      required: false,
    },
    /**
     * The destination is pinned by the caller (packages with built-in
     * tasks/pages install into the personal org/workspace only): the
     * org/workspace pickers are replaced with a note.
     */
    destinationLocked: { type: Boolean, required: false, default: false },
    pkg: {
      type: Object as PropType<Package | null>,
      required: false,
      default: null,
    },
    dbItems: {
      type: Array as PropType<
        Array<{
          packagedDbId: string;
          packagedName: string;
          packagedIcon: string | null;
          workspaceName: string | null;
          workspaceIcon: string | null;
          mode: "create" | "extend" | "use";
          willAddCount: number;
          isMissingMapping: boolean;
          isRequired: boolean;
          objectsCount: number;
          sortKey: string;
        }>
      >,
      required: false,
      default: () => [],
    },
    workspaceDbDict: {
      type: Object as PropType<GraphDatabaseDictionary | undefined>,
      required: false,
    },
    /**
     * Agent to own the imported root automation (packages never carry the
     * source account's agent). Only relevant when the package root is an
     * automation; `null` imports it as an orphan.
     */
    automationAgentId: {
      type: String as PropType<AnyAgentId | null>,
      required: false,
      default: null,
    },
    modeByDbId: {
      type: Object as PropType<Record<string, Mode>>,
      required: true,
    },
    targetByDbId: {
      type: Object as PropType<Record<string, TableId | undefined>>,
      required: true,
    },
    importObjectsEnabledByDbId: {
      type: Object as PropType<Record<string, boolean>>,
      required: true,
    },
    setImportObjectsEnabled: {
      type: Function as PropType<(id: string, enabled: boolean) => void>,
      required: true,
    },
    setMode: {
      type: Function as PropType<(id: string, mode: Mode) => void>,
      required: true,
    },
    setTarget: {
      type: Function as PropType<
        (id: string, target: TableId | undefined) => void
      >,
      required: true,
    },
  },
  emits: {
    "update:orgId": (_v: OrgId | undefined) => true,
    "update:workspaceId": (_v: WorkspaceId | undefined) => true,
    "update:automationAgentId": (_v: AnyAgentId | null) => true,
  },
  setup(props, { emit }) {
    const controller = inject.controller();
    const agents = inject.agents();
    const automations = inject.automations();

    const orgIdModel = computed<OrgId | undefined>({
      get: () => props.orgId,
      set: (v) => emit("update:orgId", v),
    });
    const workspaceIdModel = computed<WorkspaceId | undefined>({
      get: () => props.workspaceId,
      set: (v) => emit("update:workspaceId", v),
    });

    // --- Organizations ---
    const orgsLoading = ref(true);
    const orgList = ref<Array<{ id: OrgId; name: string; personal: boolean }>>(
      []
    );

    onMounted(async () => {
      try {
        const orgIdsRef = await controller.organization.all();
        const ids = orgIdsRef.value ?? [];
        orgIdsRef.release();
        const loaded = await Promise.all(
          ids.map(async (id) => {
            const orgRef = await controller.organization.get(id);
            const org = orgRef.value;
            orgRef.release();
            return org;
          })
        );
        orgList.value = loaded
          .filter((o): o is FullOrganization => o != null)
          .sort((a, b) => {
            const pa = a.personal ? 0 : 1;
            const pb = b.personal ? 0 : 1;
            return pa - pb || a.name.localeCompare(b.name);
          });
        // Pre-select when there is only one organization to choose from.
        if (!orgIdModel.value && orgList.value.length === 1) {
          orgIdModel.value = orgList.value[0].id;
        }
      } finally {
        orgsLoading.value = false;
      }
    });

    const orgOptions = computed<SelectOption[]>(() =>
      orgList.value.map((org) => ({
        value: org.id,
        label: org.personal ? `${org.name} (Private)` : org.name,
        aliases: [],
      }))
    );

    // --- Workspaces ---
    const workspacesLoading = ref(false);
    const workspaceList = ref<
      Array<{ id: WorkspaceId; name: string; default: boolean }>
    >([]);

    watch(
      orgIdModel,
      async (newOrgId) => {
        workspaceList.value = [];
        if (newOrgId) {
          workspacesLoading.value = true;
          try {
            const wsIdsRef =
              await controller.organization.workspaces.all(newOrgId);
            const ids = wsIdsRef.value ?? [];
            wsIdsRef.release();
            const loaded = await Promise.all(
              ids.map(async (id) => {
                const wsRef = await controller.workspace.get(id);
                const ws = wsRef.value;
                wsRef.release();
                return ws;
              })
            );
            workspaceList.value = loaded
              .filter((ws): ws is FullWorkspace => ws != null)
              .sort(
                (a, b) =>
                  Number(b.default) - Number(a.default) ||
                  a.name.localeCompare(b.name)
              );
            // Pre-select when there is only one workspace to choose from.
            if (!workspaceIdModel.value && workspaceList.value.length === 1) {
              workspaceIdModel.value = workspaceList.value[0].id;
            }
          } finally {
            workspacesLoading.value = false;
          }
        }
      },
      { immediate: true }
    );

    const workspaceOptions = computed<SelectOption[]>(() =>
      workspaceList.value.map((ws) => ({
        value: ws.id,
        label: ws.default ? `${ws.name} (Default)` : ws.name,
        aliases: [],
      }))
    );

    // --- Agent picker (root-automation packages only) ---
    // Packages never carry the source account's agent, so offer to attach the
    // imported automation to one of the destination organization's agents.
    const showAgentPicker = computed(
      () => props.pkg?.root.kind === "automation"
    );

    // The personal assistant is not part of `agents.active`; offer it in its
    // own (personal) organization so imported automations can attach to it.
    const assistantOption = computed<SelectOption | null>(() => {
      const assistant = automations.value?.assistant.value;
      if (!assistant || assistant.organizationId !== orgIdModel.value) {
        return null;
      }
      return {
        value: assistant.agentId,
        label: "Assistant",
        aliases: [assistant.agentId, "Assistant"],
      } satisfies SelectOption;
    });

    const agentOptions = computed<SelectOption[]>(() => {
      const orgId = orgIdModel.value;
      const activeAgents = orgId ? (agents.value?.active?.[orgId] ?? []) : [];
      return [
        { value: "", label: "No agent", aliases: [], separatorAfter: true },
        ...(assistantOption.value ? [assistantOption.value] : []),
        ...activeAgents.map(
          (agent) =>
            ({
              value: agent.id,
              label: agent.name ?? "Unnamed agent",
              aliases: [agent.id, agent.name ?? ""].filter(Boolean),
            }) satisfies SelectOption
        ),
      ];
    });

    const onAgentSelected = (value: string) => {
      emit("update:automationAgentId", value || null);
    };

    // Drop a selection that no longer exists (e.g. the organization changed).
    watch(agentOptions, (options) => {
      const selected = props.automationAgentId;
      if (!selected) return;
      if (options.some((option) => option.value === selected)) return;
      emit("update:automationAgentId", null);
    });

    const conflictByDbId = computed<Record<string, boolean>>(() => {
      const pkg = props.pkg;
      const dict = props.workspaceDbDict ?? {};
      if (!pkg) return {};

      const out: Record<string, boolean> = {};
      for (const db of props.dbItems) {
        const id = db.packagedDbId;
        const mode = props.modeByDbId[id] ?? "create";
        if (mode === "create") continue;

        const target = props.targetByDbId[id] ?? id;
        const workspaceDb = dict[String(target)];
        if (!workspaceDb) continue;

        const packagedDb = pkg.databases?.[id];
        if (!packagedDb) continue;

        const diff = typeDiff({
          packagedSchema: packagedDb.schema,
          workspaceSchema: workspaceDb,
        });
        if (diff.some((p) => p.isConflict)) out[id] = true;
      }

      return out;
    });

    const hiddenTargetsByPackagedDbId = computed<
      Record<string, Array<TableId>>
    >(() => {
      const dict = props.workspaceDbDict ?? {};

      const used = new Set<TableId>();
      for (const db of props.dbItems) {
        const id = db.packagedDbId;
        const mode = props.modeByDbId[id] ?? "create";
        if (mode === "create") continue;
        const explicit = props.targetByDbId[id];
        if (explicit) used.add(explicit);
        else if (dict[id]) used.add(id);
      }

      const out: Record<string, Array<TableId>> = {};
      for (const db of props.dbItems) {
        const id = db.packagedDbId;
        const mode = props.modeByDbId[id] ?? "create";
        if (mode === "create") {
          out[id] = [];
          continue;
        }

        const explicit = props.targetByDbId[id];
        const own = explicit ?? (dict[id] ? id : undefined);
        out[id] = Array.from(used).filter((t) => t !== own);
      }
      return out;
    });

    return {
      agentOptions,
      conflictByDbId,
      hiddenTargetsByPackagedDbId,
      onAgentSelected,
      orgIdModel,
      orgOptions,
      orgsLoading,
      showAgentPicker,
      workspaceIdModel,
      workspaceOptions,
      workspacesLoading,
    };
  },
});
</script>

<style scoped lang="scss">
.import-configure {
  .pickers {
    margin-top: 24px;
    display: flex;
    flex-direction: column;
    row-gap: 10px;
  }

  .destination-note {
    margin-top: 24px;
    @include themed() {
      color: t($grey-70);
    }
  }

  .agent-picker {
    margin-top: 10px;
  }

  .dbs {
    margin-top: 18px;
  }

  .section-title {
    @extend %font-caption-medium;
    margin-bottom: 8px;
    @include themed() {
      color: t($grey-70);
    }
  }

  .db-table {
    display: flex;
    flex-direction: column;
    row-gap: 8px;
  }

  .db {
    border-radius: $border-radius-m;
    @include themed() {
      border: 1px solid t($grey-15);
      background: t($bg);
    }
  }

  .db > summary {
    list-style: none;
  }

  .db > summary::-webkit-details-marker {
    display: none;
  }

  .db-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    column-gap: 12px;
    padding: 8px 10px;
    @include transition(background 0.1s ease-out);

    &:hover {
      @include themed() {
        background: t($grey-10);
      }
    }
  }

  .db-left {
    display: flex;
    align-items: center;
    column-gap: 10px;
    min-width: 0;
  }

  .icon {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }

  .icon.secondary {
    width: 16px;
    height: 16px;
  }

  .icon.placeholder {
    width: 18px;
    height: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    @include themed() {
      color: t($grey-50);
    }
  }

  .icon.placeholder.secondary {
    width: 16px;
    height: 16px;
  }

  .db-title {
    display: flex;
    align-items: baseline;
    column-gap: 8px;
    min-width: 0;
  }

  .db-arrow {
    @extend %font-caption-regular;
    flex-shrink: 0;
    @include themed() {
      color: t($grey-60);
    }
  }

  .db-name {
    @extend %font-caption-medium;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    @include themed() {
      color: t($grey-90);
    }
  }

  .db-name.secondary {
    @include themed() {
      color: t($grey-70);
    }
  }

  .new {
    @extend %font-caption-medium;
    flex-shrink: 0;
    padding: 1px 6px;
    color: $accepted;
    border-radius: $border-radius-m;
    @include themed() {
      background: rgba($accepted, 0.08);
    }
  }

  .conflict-badge {
    @extend %font-caption-medium;
    border-radius: 999px;
    padding: 2px 8px;
    white-space: nowrap;
    flex-shrink: 0;
    color: $declined;
    background: rgba($declined, 0.08);
    border: 1px solid rgba($declined, 0.25);
  }

  .db-right {
    display: inline-flex;
    align-items: center;
    column-gap: 8px;
  }

  .db-count {
    @extend %font-caption-medium;
    flex-shrink: 0;
    @include themed() {
      color: t($grey-80);
    }
  }

  .db-count.plus {
    color: $accepted;
  }

  .chevron {
    display: inline-flex;
    flex-shrink: 0;
    width: 10px;
    height: 10px;
    transform: rotate(-90deg);
    transform-origin: 50% 50%;
    @include transition(transform 0.1s ease-out);
    @include themed() {
      color: t($grey-50);
    }

    :deep(.fill) {
      fill: currentColor;
    }
  }

  details[open] > summary .chevron {
    transform: rotate(0deg);
  }

  .db-details {
    padding: 0 12px 12px 12px;
    @include themed() {
      border-top: 1px solid t($grey-10);
    }
  }

  .toggle {
    flex-shrink: 0;
    min-width: 92px;
  }

  /* Only show the database name once (in the summary row). */
  .db-details :deep(.import-db .header) {
    display: none;
  }

  .db-details :deep(.import-db) {
    border: 0;
    padding: 0;
  }
}
</style>
